import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import type { OpenIdConfiguration } from 'angular-auth-oidc-client';
import { LogLevel, StsConfigHttpLoader } from 'angular-auth-oidc-client';
import { map } from 'rxjs/operators';
import type { SsoConfigResponse, CorrectedSsoConfig } from './auth-config.interface';

const OIDC_DEBUG_STORAGE_KEY = 'oidc-log-level';
const VALID_LOG_LEVELS = new Set(['None', 'Debug', 'Warn', 'Error'] as const);
type LogLevelString = 'None' | 'Debug' | 'Warn' | 'Error';

function stringToLogLevel(value: string): LogLevel | null {
  const normalized = (value.charAt(0).toUpperCase() +
    value.slice(1).toLowerCase()) as LogLevelString;

  if (!VALID_LOG_LEVELS.has(normalized)) {
    return null;
  }

  return LogLevel[normalized as keyof typeof LogLevel];
}

function getOidcLogLevelOverride(): LogLevel | null {
  try {
    const value = globalThis.localStorage.getItem(OIDC_DEBUG_STORAGE_KEY);

    if (!value) {
      return null;
    }

    const logLevel = stringToLogLevel(value);
    return logLevel;
  } catch {
    return null;
  }
}

function getOidcLogLevel(): LogLevel {
  const override = getOidcLogLevelOverride();

  if (override !== null) {
    return override;
  }

  const logLevel = stringToLogLevel(environment.enableOidcDebugLogging);
  return logLevel ?? LogLevel.None;
}

export function authConfigFactory(httpClient: HttpClient): StsConfigHttpLoader {
  const baseTag = document.querySelector('base');
  const href = baseTag?.getAttribute('href');
  const baseHref = href ?? '';

  const config$ = httpClient.get<SsoConfigResponse>(environment.SSOConfig.SSOURL).pipe(
    map((configCorrection: SsoConfigResponse): CorrectedSsoConfig => {
      return {
        ...configCorrection,
        authority: configCorrection.issuer,
        client_id: environment.SSOConfig.client,
        redirect_url: globalThis.location.origin + baseHref,
        startup_route: globalThis.location.origin + baseHref,
        unauthorized_route: globalThis.location.origin + baseHref,
        forbidden_route: globalThis.location.origin + baseHref,
        post_logout_redirect_uri: globalThis.location.origin + baseHref,
        response_type: 'code',
        scope: 'openid profile email',
        silent_renew: true,
        use_refresh_token: false,
        start_checksession: false,
        renew_time_before_token_expires_in_seconds: 300,
        max_id_token_iat_offset_allowed_in_seconds: 3 * 60 * 60,
      };
    }),
    map((customConfig: CorrectedSsoConfig): OpenIdConfiguration[] => {
      const mappedConfiguration: OpenIdConfiguration = {
        authority: customConfig.authority ?? environment.SSOConfig.stsServer,
        clientId: customConfig.client_id,
        forbiddenRoute: customConfig.forbidden_route,
        historyCleanupOff: true,
        logLevel: getOidcLogLevel(),
        maxIdTokenIatOffsetAllowedInSeconds:
          customConfig.max_id_token_iat_offset_allowed_in_seconds,
        customParamsAuthRequest: {
          ui_locales: 'en',
        },
        postLoginRoute: customConfig.startup_route,
        postLogoutRedirectUri: customConfig.post_logout_redirect_uri,
        redirectUrl: customConfig.redirect_url,
        responseType: customConfig.response_type,
        scope: customConfig.scope,
        useRefreshToken: customConfig.use_refresh_token,
        silentRenew: customConfig.silent_renew,
        silentRenewUrl: customConfig.redirect_url + 'silent-renew.html',
        startCheckSession: customConfig.start_checksession,
        renewTimeBeforeTokenExpiresInSeconds:
          customConfig.renew_time_before_token_expires_in_seconds,
        unauthorizedRoute: customConfig.unauthorized_route,
        ignoreNonceAfterRefresh: true,
      };
      const bimplusConfig: OpenIdConfiguration = {
        ...mappedConfiguration,
        configId: 'bimplus',
      };
      return [bimplusConfig];
    })
  );

  return new StsConfigHttpLoader(config$);
}
